/**
 * CR-8624: save a Stock Planning remark.
 *
 * A remark belongs to one part from one supplier. On a single supplier's grid
 * that supplier is the one picked in the filters; on the "All call-off
 * suppliers" grid each row names its own. After a save, the row shows the new
 * text and who changed it, without reloading the page.
 */
import { useCallback, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { StockPlanningItem } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { toast } from 'sonner';

/**
 * The Stock Planning grid's remark saving.
 *
 * @example
 *   const { saveRemark, isRemarkSaving } = useStockPlanningRemark(setItems, filters.supplierId);
 * @param setItems - the grid's row setter, patched with the saved remark
 * @param selectedSupplierId - the supplier picked in the filters ('all' for every one)
 * @returns `saveRemark(item, text)` (resolves true once saved) and `isRemarkSaving(item)`
 */
export function useStockPlanningRemark(
  setItems: Dispatch<SetStateAction<StockPlanningItem[]>>,
  selectedSupplierId: string,
) {
  const [savingKeys, setSavingKeys] = useState<ReadonlySet<string>>(new Set());

  const supplierOf = useCallback(
    (item: StockPlanningItem) => item.supplier_id ?? Number(selectedSupplierId),
    [selectedSupplierId],
  );

  const saveRemark = useCallback(async (item: StockPlanningItem, text: string): Promise<boolean> => {
    const supplierId = supplierOf(item);
    const key = `${item.id}:${supplierId}`;
    setSavingKeys(prev => new Set(prev).add(key));
    try {
      const { data } = await callOffAPI.setStockPlanningRemark(item.id, supplierId, text.trim());
      setItems(prev => prev.map(row => (
        row.id === data.part_id && supplierOf(row) === data.supplier_id
          ? {
            ...row,
            remarks: data.remarks,
            remark_updated_by_name: data.remark_updated_by_name,
            remark_updated_at: data.remark_updated_at,
          }
          : row
      )));
      toast.success(data.remarks
        ? `Remark saved for ${item.part_number}`
        : `Remark cleared for ${item.part_number}`);
      return true;
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Could not save the remark'));
      return false;
    } finally {
      setSavingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [setItems, supplierOf]);

  const isRemarkSaving = useCallback(
    (item: StockPlanningItem) => savingKeys.has(`${item.id}:${supplierOf(item)}`),
    [savingKeys, supplierOf],
  );

  return { saveRemark, isRemarkSaving };
}
